import React from 'react';

import { I18n } from '@fiverr-private/i18n-react';
import { Typography } from '@fiverr-private/typography';
import { Divider, Stack, Container } from '@fiverr-private/layout_components';
import { Tooltip } from '@fiverr-private/tooltip';
import { Button } from '@fiverr-private/button';
import { useComplianceContext } from '../../ComplianceProvider/context';
import { calculateEstimationDate, formatDate } from '../../../utils';
import { WorkforceClassificationRequestStatuses } from '../../../types/complianceTypes';
import { ComplianceTypes } from '../../ComplianceModal/types';
import {
    getWorkforceClassificationStatus,
    getWorkforceClassificationStatusTooltip,
} from '../../ComplianceProvider/utils';
import { useSellerConfigData } from '../../../api/useSellerConfigData';
import { reportEventsApi } from '../../../biEvents';
import { CollapsibleComplianceTemplate } from './CollapsibleComplianceTemplate';

const ESTIMATE_DAYS = 16;

export const WorkforceClassificationCollapsibleSection = ({
    requeseteeId,
    requeseteeUserName,
}: {
    requeseteeId: number;
    requeseteeUserName: string;
}) => {
    const context = useComplianceContext();
    const { classificationRequestorUserName } = useSellerConfigData(requeseteeId);
    const openModalOptions = {
        sellerId: requeseteeId,
        sellerName: requeseteeUserName,
    };

    if (!context) return null;

    const sellerComplianceState = context.getSellerComplianceState(requeseteeId);
    const workforceClassificationData = sellerComplianceState.classificationRequestData;
    const requestStatus = workforceClassificationData?.requestStatus as WorkforceClassificationRequestStatuses;
    const isWithBackgroundCheckAction = [
        WorkforceClassificationRequestStatuses.COMPLETED,
        WorkforceClassificationRequestStatuses.EXPIRED,
        WorkforceClassificationRequestStatuses.CANCELED,
    ].includes(requestStatus);
    const visibleStatus = getWorkforceClassificationStatus(workforceClassificationData);
    const statusTooltip = getWorkforceClassificationStatusTooltip(requeseteeUserName, workforceClassificationData);
    const isWithEstimatedDate = [
        WorkforceClassificationRequestStatuses.IN_PROGRESS,
        WorkforceClassificationRequestStatuses.REQUESTED,
    ].includes(requestStatus);
    const updatedAt = workforceClassificationData?.updatedAt;
    const lastStatusHistoryUpdateTimeStamp = updatedAt ? new Date(updatedAt).getTime() : undefined;
    const lastStatusDisplayDate = lastStatusHistoryUpdateTimeStamp ? new Date(lastStatusHistoryUpdateTimeStamp) : '';

    const deliveryDate = formatDate(
        isWithEstimatedDate
            ? calculateEstimationDate(lastStatusHistoryUpdateTimeStamp, ESTIMATE_DAYS)
            : lastStatusDisplayDate
    );
    return (
        <CollapsibleComplianceTemplate
            complianceType={ComplianceTypes.WORKFORCE_CLASSIFICATION}
            status={visibleStatus}
            date={deliveryDate}
        >
            <Divider />
            <Stack paddingX={'5'} direction={'column'} gap={'3'} paddingY={'4'}>
                {classificationRequestorUserName && (
                    <Stack justifyContent={'spaceBetween'}>
                        <Typography fontSize="b_sm">
                            <I18n k={`compliance.compliance_section.classification.requestor`} />
                        </Typography>
                        <Typography textAlign={'end'} color={'grey_1200'} fontSize="b_sm">
                            {sellerComplianceState.isClassificationRequestorYou ? (
                                <I18n k={'compliance.compliance_section.classification.you'} />
                            ) : (
                                classificationRequestorUserName
                            )}
                        </Typography>
                    </Stack>
                )}
                <Stack justifyContent={'spaceBetween'}>
                    <Typography fontSize="b_sm">
                        <I18n k={`compliance.compliance_section.classification.status`} />
                    </Typography>
                    <Tooltip position="bottom" content={statusTooltip}>
                        {visibleStatus}
                    </Tooltip>
                </Stack>
                <Stack justifyContent={'spaceBetween'}>
                    <Typography fontSize="b_sm">
                        <I18n
                            k={`compliance.compliance_section.classification.${
                                isWithEstimatedDate ? 'deliveryDateEstimated' : 'deliveryDate'
                            }`}
                        />
                    </Typography>
                    <Typography color={'grey_1200'} fontSize="b_sm">
                        {deliveryDate}
                    </Typography>
                </Stack>
            </Stack>
            {isWithBackgroundCheckAction && (
                <Container>
                    <Divider />
                    <Stack paddingX={'5'} alignItems={'center'} justifyContent={'flexEnd'} paddingY={'4'}>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                reportEventsApi.clickedOnRequestAudit({
                                    pageName: context.pageName,
                                    sellerId: requeseteeId,
                                    type: 'section',
                                });

                                context?.openWFCModal(openModalOptions);
                            }}
                        >
                            <I18n k={`compliance.compliance_section.classification_action`} />
                        </Button>
                    </Stack>
                </Container>
            )}
        </CollapsibleComplianceTemplate>
    );
};
